import { useState, useEffect, useCallback, useRef } from "react";
import { getIdToken } from "firebase/auth";
import { auth } from "../firebase";
import { syncLeetCode, createSyncToken, pollSyncStatus } from "../services/api";

const SELF_ORIGIN = window.location.origin;

const BASE_URL = "https://contests-ladder-backend.onrender.com";

/* ── Bookmarklet source ─────────────────────────────────────────────────────── */
const BOOKMARKLET_SRC = `(async function(){
const p=new URLSearchParams(location.search),t=p.get('rp');
if(!t){alert('Open this page from RoundPrep to sync.');return;}
const g=async(q,v={})=>{const c=document.cookie.split('; ').find(r=>r.startsWith('csrftoken=')),csrf=c?c.split('=')[1]:'';return(await fetch('/graphql',{method:'POST',credentials:'include',headers:{'Content-Type':'application/json','x-csrftoken':csrf},body:JSON.stringify({query:q,variables:v})})).json()};
const s=await g('query{userStatus{username isSignedIn}}');
if(!s.data.userStatus.isSignedIn){alert('Please log into LeetCode first.');return;}
const u=s.data.userStatus.username;
const[pr,cr]=await Promise.all([g('query userProgressQuestionList($f:UserProgressQuestionListInput){userProgressQuestionList(filters:$f){questions{titleSlug questionStatus difficulty lastSubmittedAt numSubmitted}}}',{f:{skip:0,limit:2000}}),g('query c($u:String!){userContestRankingHistory(username:$u){attended rating}}',{u})]);
const qs=(pr.data.userProgressQuestionList.questions||[]).filter(q=>q.questionStatus==='SOLVED'||q.questionStatus==='ATTEMPTED');
const att=(cr.data.userContestRankingHistory||[]).filter(e=>e.attended);
const r=await fetch('${BASE_URL}/api/leetcode/sync-via-token',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({token:t,leetcodeUsername:u,questions:qs,contestRating:att.length?Math.round(att[att.length-1].rating):null,contestsAttended:att.length,ratingHistory:att.map(e=>Math.round(e.rating))})});
const d=await r.json();
if(r.ok){const el=document.createElement('div');el.style.cssText='position:fixed;top:20px;right:20px;z-index:99999;background:#0f172a;border:1px solid rgba(45,212,191,0.5);border-radius:12px;padding:16px 20px;color:#2dd4bf;font-family:system-ui;font-size:14px;font-weight:600;box-shadow:0 8px 32px rgba(0,0,0,0.5)';el.textContent='✓ RoundPrep synced! You can close this tab.';document.body.appendChild(el);setTimeout(()=>window.close(),2500);}else{alert('Sync failed: '+(d.error||'Unknown'));}
})();`;

const BOOKMARKLET_HREF = `javascript:${encodeURIComponent(BOOKMARKLET_SRC)}`;

/* ──────────────────────────────────────────────────────────────────────────── */
/*  Inline styles (matches the app's glass / dark aesthetic)                    */
/* ──────────────────────────────────────────────────────────────────────────── */

const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(3,4,10,0.82)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  card: {
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.025) 100%)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 24,
    boxShadow:
      "0 32px 80px rgba(0,0,0,0.7), 0 1px 0 rgba(255,255,255,0.06) inset",
    padding: "36px 32px 28px",
    width: "100%",
    maxWidth: 420,
    color: "#e2e8f0",
    position: "relative",
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    fontSize: 20,
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: 6,
  },
  title: {
    fontFamily: "var(--font-display)",
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 4,
    color: "#f1f5f9",
  },
  subtitle: {
    fontSize: 14,
    color: "#94a3b8",
    marginBottom: 24,
    lineHeight: 1.6,
  },
  benefit: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 14,
    color: "#cbd5e1",
    marginBottom: 8,
  },
  checkIcon: { color: "#2dd4bf", fontWeight: 700, fontSize: 15 },
  divider: { borderTop: "1px solid rgba(255,255,255,0.07)", margin: "20px 0" },
  btnPrimary: {
    width: "100%",
    padding: "12px 0",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    transition: "opacity 0.15s",
    fontFamily: "var(--font-display)",
  },
  btnSecondary: {
    width: "100%",
    marginTop: 10,
    padding: "10px 0",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "transparent",
    color: "#64748b",
    fontSize: 14,
    cursor: "pointer",
    transition: "color 0.15s",
  },
  infoBox: {
    background: "rgba(96,165,250,0.07)",
    border: "1px solid rgba(96,165,250,0.18)",
    borderRadius: 14,
    padding: "16px",
    fontSize: 14,
    color: "#94a3b8",
    lineHeight: 1.7,
    marginBottom: 20,
  },
  successBox: {
    background: "rgba(45,212,191,0.07)",
    border: "1px solid rgba(45,212,191,0.2)",
    borderRadius: 14,
    padding: "20px",
    marginBottom: 20,
  },
  stat: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    fontSize: 14,
    color: "#94a3b8",
  },
  statVal: { color: "#2dd4bf", fontWeight: 700, fontSize: 16 },
  errorBox: {
    background: "rgba(248,113,113,0.08)",
    border: "1px solid rgba(248,113,113,0.2)",
    borderRadius: 14,
    padding: "14px",
    color: "#f87171",
    fontSize: 13,
    marginBottom: 16,
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.08)",
    borderTop: "2px solid rgba(96,165,250,0.8)",
    animation: "spin 0.7s linear infinite",
    margin: "0 auto 12px",
  },
};

/* ──────────────────────────────────────────────────────────────────────────── */
/*  Screens                                                                      */
/* ──────────────────────────────────────────────────────────────────────────── */

function WelcomeScreen({ onConnect, onSkip }) {
  return (
    <>
      <h2 style={S.title}>Welcome to RoundPrep</h2>
      <p style={S.subtitle}>
        Import your LeetCode progress to personalise your practice.
      </p>

      {[
        "Import solved problems",
        "Import attempted problems",
        "Your Contest coverage analytics",
      ].map((b) => (
        <div key={b} style={S.benefit}>
          <span style={S.checkIcon}>✓</span>
          <span>{b}</span>
        </div>
      ))}

      <div style={S.divider} />

      <button style={S.btnPrimary} onClick={onConnect}>
        Connect LeetCode
      </button>
      <button style={S.btnSecondary} onClick={onSkip}>
        Skip for now
      </button>
    </>
  );
}

/** Shared step indicator pill */
function StepPill({ current, total }) {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 16 }}>
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            width: i + 1 === current ? 24 : 8,
            height: 6,
            borderRadius: 3,
            background: i + 1 === current
              ? "linear-gradient(90deg, #2dd4bf, #0891b2)"
              : i + 1 < current
                ? "rgba(45,212,191,0.35)"
                : "rgba(255,255,255,0.1)",
            transition: "all 0.25s",
          }}
        />
      ))}
    </div>
  );
}

/** When extension is absent, jump straight to bookmarklet overview. */
function ExtensionPromptScreen({ onBack, onUseBookmarklet }) {
  useEffect(() => { onUseBookmarklet(); }, [onUseBookmarklet]);
  return <LoadingScreen message="Setting up…" />;
}
function BookmarkletOverviewScreen({ onNext, onBack }) {
  return (
    <>
      <h2 style={{ ...S.title, fontSize: 20 }}>Sync via Bookmarklet</h2>
      <p style={{ ...S.subtitle, marginBottom: 20 }}>
        No extension needed. Works on any browser. Free.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
        {[
          {
            n: "1", color: "#2dd4bf", icon: "🔖",
            title: "Add a bookmark",
            desc: "Drag a button to your browser's bookmarks bar. One-time setup.",
          },
          {
            n: "2", color: "#60a5fa", icon: "🌐",
            title: "RoundPrep opens LeetCode",
            desc: "We open leetcode.com for you automatically with a secure sync code.",
          },
          {
            n: "3", color: "#c084fc", icon: "⚡",
            title: "Click the bookmark",
            desc: "Your solved problems and contest data sync instantly. Tab closes on its own.",
          },
        ].map(({ n, color, icon, title, desc }) => (
          <div key={n} style={{ display: "flex", gap: 14, padding: "12px 14px", borderRadius: 14, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", alignItems: "flex-start" }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `rgba(${color === "#2dd4bf" ? "45,212,191" : color === "#60a5fa" ? "96,165,250" : "192,132,252"},0.12)`, border: `1px solid rgba(${color === "#2dd4bf" ? "45,212,191" : color === "#60a5fa" ? "96,165,250" : "192,132,252"},0.25)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>{icon}</div>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 13.5, color: "#e2e8f0", letterSpacing: "-0.01em", marginBottom: 3 }}>
                <span style={{ color, marginRight: 6, fontSize: 11 }}>STEP {n}</span>{title}
              </div>
              <div style={{ fontSize: 12.5, color: "rgba(148,163,184,0.6)", lineHeight: 1.55 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      <button style={S.btnPrimary} onClick={onNext}>
        Let's do it — Start Setup →
      </button>
      <button style={S.btnSecondary} onClick={onBack}>← Back</button>
    </>
  );
}

function BookmarkletSetupScreen({ onStartSync, onBack }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(BOOKMARKLET_HREF).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <>
      <StepPill current={1} total={2} />
      <h2 style={{ ...S.title, fontSize: 18 }}>Step 1 — Add the Bookmark</h2>
      <p style={{ ...S.subtitle, marginBottom: 12 }}>Do this once. You won't need to do it again.</p>

      <BookmarkletDiagram />

      {/* Option A — Drag */}
      <div style={{ ...S.infoBox, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 12.5, color: "#2dd4bf", marginBottom: 8, letterSpacing: "0.02em", textTransform: "uppercase" }}>Option A — Drag to bookmarks bar</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            dangerouslySetInnerHTML={{
              __html: `<a href="${BOOKMARKLET_HREF.replace(/"/g, "&quot;")}"
                draggable="true"
                style="display:inline-flex;align-items:center;gap:7px;padding:9px 18px;border-radius:9px;background:linear-gradient(135deg,#2dd4bf,#0891b2);color:#fff;font-family:system-ui;font-weight:700;font-size:13px;text-decoration:none;cursor:grab;box-shadow:0 0 14px rgba(45,212,191,0.2);white-space:nowrap;flex-shrink:0">
                🔖 RoundPrep Sync
              </a>`
            }}
          />
          <div style={{ fontSize: 12, color: "rgba(148,163,184,0.6)", lineHeight: 1.55 }}>
            Drag this to the <strong style={{ color: "#e2e8f0" }}>bookmarks bar</strong>.<br />
            <span style={{ color: "rgba(248,113,113,0.65)" }}>⚠ Don't click it here — only drag.</span>
          </div>
        </div>
      </div>

      {/* Option B — Manual */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(148,163,184,0.3)", marginBottom: 8, fontFamily: "var(--font-display)" }}>Option B — Manual (most reliable)</div>
        <div style={{ fontSize: 12.5, color: "rgba(148,163,184,0.65)", lineHeight: 1.8, marginBottom: 8 }}>
          Press <kbd style={{ background: "rgba(255,255,255,0.08)", borderRadius: 3, padding: "0 5px", fontSize: 11, color: "#e2e8f0" }}>Cmd+D</kbd> / <kbd style={{ background: "rgba(255,255,255,0.08)", borderRadius: 3, padding: "0 5px", fontSize: 11, color: "#e2e8f0" }}>Ctrl+D</kbd> → name it <strong style={{ color: "#e2e8f0" }}>RoundPrep Sync</strong> → replace the URL with:
        </div>
        <button
          onClick={handleCopy}
          style={{ width: "100%", padding: "9px 0", borderRadius: 8, background: copied ? "rgba(45,212,191,0.12)" : "rgba(255,255,255,0.04)", border: `1px solid ${copied ? "rgba(45,212,191,0.4)" : "rgba(255,255,255,0.09)"}`, color: copied ? "#2dd4bf" : "#94a3b8", fontSize: 13, fontFamily: "var(--font-display)", fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
        >
          {copied ? "✓ Copied!" : "📋 Copy Bookmarklet Code"}
        </button>
      </div>

      <button style={S.btnPrimary} onClick={onStartSync}>
        Done — Open LeetCode to Sync →
      </button>
      <button style={S.btnSecondary} onClick={onBack}>← Back</button>
    </>
  );
}

function BookmarkletDiagram() {
  return (
    <div style={{ margin: "12px 0", borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
      {/* Fake browser chrome */}
      <div style={{ background: "rgba(15,23,42,0.9)", padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(248,113,113,0.6)" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(251,191,36,0.6)" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(45,212,191,0.6)" }} />
        <div style={{ flex: 1, margin: "0 12px", background: "rgba(255,255,255,0.06)", borderRadius: 6, padding: "3px 10px", fontSize: 11, color: "rgba(148,163,184,0.5)" }}>
          leetcode.com/?rp=…
        </div>
      </div>
      {/* Bookmarks bar */}
      <div style={{ background: "rgba(15,23,42,0.7)", padding: "5px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
        <span style={{ color: "rgba(148,163,184,0.4)" }}>Bookmarks:</span>
        <span style={{ padding: "2px 8px", borderRadius: 5, background: "rgba(45,212,191,0.15)", border: "1px solid rgba(45,212,191,0.35)", color: "#2dd4bf", fontWeight: 600 }}>
          🔖 RoundPrep Sync
        </span>
        <span style={{ color: "rgba(148,163,184,0.25)", fontSize: 10 }}>← it lives here</span>
      </div>
      {/* Page area */}
      <div style={{ padding: "14px 12px", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: "rgba(148,163,184,0.35)" }}>LeetCode page…</div>
      </div>
    </div>
  );
}

function BookmarkletWaitingScreen({ onCancel }) {
  const [showBarHelp, setShowBarHelp] = useState(false);

  return (
    <>
      <StepPill current={2} total={2} />
      <h2 style={{ ...S.title, fontSize: 19 }}>Step 2 — Sync Now</h2>

      <BookmarkletDiagram />

      {/* Account warning */}
      <div style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 12, padding: "10px 14px", marginBottom: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
        <p style={{ margin: 0, fontSize: 12.5, color: "rgba(253,230,138,0.85)", lineHeight: 1.6 }}>
          Make sure you are signed into LeetCode with your <strong style={{ color: "#fbbf24" }}>correct account</strong> before clicking the bookmarklet.
        </p>
      </div>

      {/* Step-by-step */}
      <div style={{ ...S.infoBox, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, color: "#2dd4bf", marginBottom: 12, fontSize: 13 }}>
          On the LeetCode tab that just opened:
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Step 1 */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(45,212,191,0.15)", border: "1px solid rgba(45,212,191,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#2dd4bf", flexShrink: 0, marginTop: 1 }}>1</div>
            <div style={{ fontSize: 13, color: "rgba(203,213,225,0.85)", lineHeight: 1.5 }}>
              Confirm you are signed in as the <strong style={{ color: "#fbbf24" }}>right LeetCode account</strong>.
              <br/>
              <span style={{ fontSize: 11.5, color: "rgba(148,163,184,0.5)" }}>Check the avatar / username in the top-right corner of leetcode.com.</span>
            </div>
          </div>

          {/* Step 2 */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(45,212,191,0.15)", border: "1px solid rgba(45,212,191,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#2dd4bf", flexShrink: 0, marginTop: 1 }}>2</div>
            <div style={{ fontSize: 13, color: "rgba(203,213,225,0.85)", lineHeight: 1.5 }}>
              Look for <strong style={{ color: "#2dd4bf" }}>🔖 RoundPrep Sync</strong> in the <strong>bookmarks bar</strong> — the row of bookmarks just below the URL bar.
              <br/>
              <button
                onClick={() => setShowBarHelp(v => !v)}
                style={{ marginTop: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11.5, color: "rgba(96,165,250,0.7)", textDecoration: "underline" }}
              >
                {showBarHelp ? "▲ Hide help" : "▼ Bookmarks bar not visible?"}
              </button>
              {showBarHelp && (
                <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 8, background: "rgba(96,165,250,0.07)", border: "1px solid rgba(96,165,250,0.15)", fontSize: 12, color: "rgba(148,163,184,0.8)", lineHeight: 1.7 }}>
                  Show it with:<br/>
                  • <strong style={{ color: "#e2e8f0" }}>Chrome / Edge:</strong> <kbd style={{ background: "rgba(255,255,255,0.08)", borderRadius: 3, padding: "0 4px" }}>Cmd+Shift+B</kbd> (Mac) &nbsp;/&nbsp; <kbd style={{ background: "rgba(255,255,255,0.08)", borderRadius: 3, padding: "0 4px" }}>Ctrl+Shift+B</kbd> (Win)<br/>
                  • <strong style={{ color: "#e2e8f0" }}>Firefox:</strong> <kbd style={{ background: "rgba(255,255,255,0.08)", borderRadius: 3, padding: "0 4px" }}>Cmd+Shift+B</kbd> / <kbd style={{ background: "rgba(255,255,255,0.08)", borderRadius: 3, padding: "0 4px" }}>Ctrl+Shift+B</kbd><br/>
                  • <strong style={{ color: "#e2e8f0" }}>Safari:</strong> View → Show Favourites Bar
                </div>
              )}
            </div>
          </div>

          {/* Step 3 */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(45,212,191,0.15)", border: "1px solid rgba(45,212,191,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#2dd4bf", flexShrink: 0, marginTop: 1 }}>3</div>
            <div style={{ fontSize: 13, color: "rgba(203,213,225,0.85)", lineHeight: 1.5 }}>
              <strong>Click 🔖 RoundPrep Sync.</strong>
              <br/>
              <span style={{ fontSize: 11.5, color: "rgba(148,163,184,0.5)" }}>A green "✓ RoundPrep synced!" banner appears → the tab closes automatically → this page updates.</span>
            </div>
          </div>

        </div>
      </div>

      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 24, height: 24, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.06)", borderTop: "2px solid #2dd4bf", animation: "spin 0.7s linear infinite", margin: "0 auto 8px" }} />
        <p style={{ color: "rgba(148,163,184,0.5)", fontSize: 12 }}>Waiting for bookmarklet click…</p>
      </div>
      <button style={S.btnSecondary} onClick={onCancel}>Cancel</button>
    </>
  );
}

function LoadingScreen({ message }) {
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={S.spinner} />
      <p style={{ color: "#94a3b8", fontSize: 14 }}>{message}</p>
    </div>
  );
}

function SuccessScreen({ result, onDone, isReSync }) {
  return (
    <>
      <h2 style={{ ...S.title, fontSize: 19 }}>{isReSync ? "Sync Complete" : "Import Complete"}</h2>
      <div style={S.successBox}>
        {[
          ["Solved", result.solved],
          ["Attempted", result.attempted],
          ["Contest Coverage", `${result.coverage}%`],
        ].map(([label, value]) => (
          <div key={label} style={S.stat}>
            <span>{label}</span>
            <span style={S.statVal}>{value}</span>
          </div>
        ))}
      </div>
      <button style={S.btnPrimary} onClick={onDone}>
        {isReSync ? "Done" : "Start Practice →"}
      </button>
    </>
  );
}

const STORAGE_KEY = "roundprep_lc_synced";

export default function WelcomeModal({ user, onClose }) {
  const isReSync = localStorage.getItem(STORAGE_KEY) === "true";

  // "welcome" | "bookmarklet_overview" | "bookmarklet_setup" | "bookmarklet_waiting" | "success" | "error"
  const [screen, setScreen] = useState(isReSync ? "bookmarklet_setup" : "welcome");
  const [errorMsg, setErrorMsg] = useState("");
  const [syncResult, setSyncResult] = useState(null);
  const pollTimerRef = useRef(null);

  // Cleanup poll timer on unmount
  useEffect(() => () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); }, []);

  // ── Bookmarklet flow ─────────────────────────────────────────────────────────

  const handleStartBookmarkletSync = useCallback(async () => {
    try {
      const idToken = await getIdToken(auth.currentUser, false);
      const { token } = await createSyncToken(idToken);

      window.open(`https://leetcode.com/?rp=${token}`, "_blank");
      setScreen("bookmarklet_waiting");

      let attempts = 0;
      pollTimerRef.current = setInterval(async () => {
        attempts++;
        if (attempts > 60) {
          clearInterval(pollTimerRef.current);
          setErrorMsg("Sync timed out. Please try again.");
          setScreen("error");
          return;
        }
        try {
          const status = await pollSyncStatus(token);
          if (status.status === "complete") {
            clearInterval(pollTimerRef.current);
            const result = status.data;
            setSyncResult(result);
            localStorage.setItem(STORAGE_KEY, "true");
            if (result.leetcodeUsername) localStorage.setItem("lc_username", result.leetcodeUsername);
            if (result.ratingHistory) localStorage.setItem("lc_rating_history", JSON.stringify(result.ratingHistory));
            setScreen("success");
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setErrorMsg(err.message);
      setScreen("error");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSkip = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "skipped");
    onClose();
  }, [onClose]);

  function renderBody() {
    switch (screen) {
      case "welcome":
        return <WelcomeScreen onConnect={() => setScreen("bookmarklet_overview")} onSkip={handleSkip} />;

      case "bookmarklet_overview":
        return (
          <BookmarkletOverviewScreen
            onNext={() => setScreen("bookmarklet_setup")}
            onBack={() => setScreen("welcome")}
          />
        );

      case "bookmarklet_setup":
        return (
          <BookmarkletSetupScreen
            onStartSync={handleStartBookmarkletSync}
            onBack={() => setScreen(isReSync ? "welcome" : "bookmarklet_overview")}
          />
        );

      case "bookmarklet_waiting":
        return (
          <BookmarkletWaitingScreen
            onCancel={() => {
              if (pollTimerRef.current) clearInterval(pollTimerRef.current);
              setScreen("welcome");
            }}
          />
        );

      case "success":
        return <SuccessScreen result={syncResult} onDone={onClose} isReSync={isReSync} />;

      case "error":
        return (
          <>
            <div style={S.errorBox}>{errorMsg}</div>
            <button style={S.btnPrimary} onClick={() => setScreen(isReSync ? "bookmarklet_setup" : "welcome")}>
              Try Again
            </button>
            <button style={S.btnSecondary} onClick={isReSync ? onClose : handleSkip}>
              {isReSync ? "Close" : "Skip for now"}
            </button>
          </>
        );

      default:
        return null;
    }
  }

  return (
    <div style={S.backdrop}>
      <div style={S.card}>
        <button style={S.closeBtn} onClick={onClose} aria-label="Close" title="Remind me later">×</button>
        {renderBody()}
      </div>
    </div>
  );
}

/** Returns true if the modal should be shown to this user. */
export function shouldShowWelcomeModal() {
  return !localStorage.getItem(STORAGE_KEY);
}
